import * as React from 'react'
import { closeNewsModal, showConfirmModal } from '../../store/modals/actions'
import ModalMiddle from './Parts/ModalMiddle'
import TooltipError from '../Tooltips/ErrorTooltip'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ModalLoader from './Parts/ModalLoader'
import ModalContent from './Parts/ModalContent'
import { WithTranslation, withTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { Style } from '../../styles'
import { News, NewsTag, NewsTranslateableFields, CurrentUser, HelpCenter, PublicLocale, User } from '../../types'
import ModalWindow from './Parts/ModalWindow'
import ModalFooter from './Parts/ModalFooter'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import Editor, { ARTICLE_EDITOR_CONFIG } from '../Editor/Editor'
import AutogrowTextarea from '../Form/AutogrowTextArea'
import Button from '../Button/Button'
import NewsController from '../../controllers/NewsController'
import MobilityHelper from '../../helpers/MobilityHelper'
import Notification from '../../utilities/Notification'
import PowerSelect from '../Form/PowerSelect'
import HelpCenterHelper from '../../helpers/HelpCenterHelper'
import { Helmet } from 'react-helmet'
import Icon from '../Icons/Icon'
import moment from '../../utilities/Moment'
import ResourceCreatablePowerSelect from '../Form/ResourceCreatablePowerSelect'
import ArticleContent from '../../help_center/components/Article/ArticleContent'
import Dropzone from '../Dropzone/Dropzone'
import { NEWS_COVER_IMAGE_CONTENT_TYPES, NEWS_COVER_IMAGE_FILE_SIZE } from '../../Constants'
import { DropEvent, FileRejection } from 'react-dropzone'
import { ActiveStorageController } from '../../controllers'
import CoverImageContainer from '../News/CoverImageContainer'
import CoverImage from '../News/CoverImage'
import NewsTags from '../News/NewsTags'
import NewsTagItem from '../News/NewsTagItem'
import SimpleTooltip from '@/components/Tooltips/SimpleTooltip'

const NewsModalWindow = styled(ModalWindow)`
  position: relative;
  display: flex;
  flex-direction: row;
  height: 100vh;
  max-width: initial;

  @media screen and (max-width: ${Style.breakpoints.SMALL}) {
    max-width: initial;
    height: 100%;
  }

  ${ModalMiddle} {
    overflow-x: hidden;
    overflow-y: auto;
  }

  ${ModalContent} {
    padding: 40px 20px 0 20px;
  }
`

const NewsModalWindowContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const Title = styled.div`
  margin-bottom: 8px;

  textarea {
    background-color: transparent;
    border: 0;
    border-bottom: 1px solid transparent;
    padding: 0;
    box-shadow: none;
    font-size: 32px;
    line-height: 130%;
    font-weight: 700;
    resize: none;
    overflow: hidden;
    width: 100%;
    color: #1a1a1a;
    outline: none;
    border-radius: 0;

    &:focus, &:hover {
      box-shadow: none;
      outline: none;
      border: none;
      box-shadow: none;
      border-bottom: 1px dashed rgba(0,0,0,.2);
    }
  }
`

const Summary = styled.div`
  margin-bottom: 16px;
  border-bottom: 1px solid transparent;
  font-weight: 500;

  textarea {
    font-size: 18px;
    line-height: 24px;
    min-height: 30px;
    padding: 0;
    color: #646462;
    border: none;
    outline: none;
    border-radius: 0;
    border-bottom: 1px solid transparent;

    &:focus, &:hover {
      box-shadow: none;
      outline: none;
      border: none;
      box-shadow: none;
      border-bottom: 1px dashed rgba(0,0,0,.2);
    }
  }
`

const NewsModalWindowDetails = styled.div<{ active: boolean, mobileActive?: boolean }>`
  border-left: 1px solid ${Style.color.border};
  min-width: 340px;
  max-width: 340px;
  width: 340px;
  transition: transform 150ms ease-in-out;
  background: white;
  display: flex;
  flex-direction: column;
  display: none;

  ${props => props.active && css`
    display: flex;
  `}

  @media screen and (max-width: ${Style.breakpoints.SMALL}) {
    display: none;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    max-width: initial;
    width: 100%;
    min-width: initial;

    ${props => props.mobileActive && css`
      display: flex;
    `}
  }

  ${ModalContent} {
    padding: 20px;
  }
`

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const ActionDivider = styled.div`
  background: #e4e5e1;
  width: 1px;
  height: 30px;
  margin: 0 8px;
`

const Action = styled.div<{ type?: 'secondary', mobile?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background: #f3f3f1;
  padding: 8px;
  min-width: 38px;
  min-height: 38px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 600;
  border-radius: 50%;

  ${props => props.mobile && css`
    display: none;

    @media screen and (max-width: ${Style.breakpoints.SMALL}) {
      display: flex;
    }
  `}

  &:hover {
    background: #e9eae6;
  }

  ${props => props.type === 'secondary' && css`
    background: transparent;

    &:hover {
      background: #e9eae6;
    }
  `}

  svg {
    width: 20px;
    height: 20px;
  }
`

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DetailsItem = styled.div<{ stacked?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 8px;

  ${props => props.stacked && css`
    flex-direction: column;
    align-items: initial;
  `}

  > div {
    flex: 1;

    &:first-child {
      color: #646462;
      font-weight: 400;
      line-height: 20px;
      font-size: 14px;
    }

    &:last-child {
      color: rgb(26, 26, 26);

      &.emoji {
        display: flex;
        flex-direction: row;
        gap: 8px;
        justify-content: center;
        align-items: center;
        margin-top: 8px;
        font-size: 22px;
        font-weight: bold;
      }
    }
  }
`

const ModalLoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`

const DropzoneContainer = styled.div`
  width: 100%;
  max-height: 200px;
`

interface IStateToProps {
  locale: PublicLocale
  newsCenterId: string
  currentUser: CurrentUser
  news: News
  onSubmit?: (news: News) => void
  onDelete?: (news: News) => void
}

interface IDispatchToProps {
  close: typeof closeNewsModal
  showConfirmModal: typeof showConfirmModal
}

type IProps = IDispatchToProps & IStateToProps & WithTranslation & RouteComponentProps

interface IState {
  didInitialLoad: boolean
  news: News
  helpCenter: HelpCenter
  activeLocale: PublicLocale
  sidepanelOpen: boolean
  mobileSidepanelOpen: boolean
  errors: any
}

class NewsModal extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    this.state = {
      didInitialLoad: false,
      news: null,
      helpCenter: null,
      activeLocale: props.locale,
      sidepanelOpen: true,
      mobileSidepanelOpen: false,
      errors: {},
    }

    this.fetchForm = this.fetchForm.bind(this)
    this.onFormSubmit = this.onFormSubmit.bind(this)
    this.onErrorsDismiss = this.onErrorsDismiss.bind(this)
    this.onCloseClick = this.onCloseClick.bind(this)
    this.onCoverImageChange = this.onCoverImageChange.bind(this)
    this.onCoverImageDeleteClick = this.onCoverImageDeleteClick.bind(this)
    this.onTitleChange = this.onTitleChange.bind(this)
    this.onSummaryChange = this.onSummaryChange.bind(this)
    this.onContentChange = this.onContentChange.bind(this)
    this.onLocaleChange = this.onLocaleChange.bind(this)
    this.onAuthorChange = this.onAuthorChange.bind(this)
    this.onTagsChange = this.onTagsChange.bind(this)
    this.onDeleteClick = this.onDeleteClick.bind(this)
    this.onToggleSidepanel = this.onToggleSidepanel.bind(this)
  }

  componentDidMount() {
    this.fetchForm()
  }

  async fetchForm() {
    const { newsCenterId, news: propsNews, locale } = this.props
    try {
      const response = await NewsController.getForm(newsCenterId, { id: propsNews ? propsNews.id : null })
      const { news, news_center } = response

      this.setState({
        didInitialLoad: true,
        news: {
          ...news,
          ...propsNews,
        },
        helpCenter: news_center,
        activeLocale: locale || (news_center.locale as PublicLocale),
      })

    } catch (ex) {
      console.error(ex)
    }
  }

  async onFormSubmit(e?: React.FormEvent) {
    e?.preventDefault();

    const { onSubmit, close, t, newsCenterId } = this.props
    const { news } = this.state

    try {
      let response = null

      if (news.id) {
        response = await NewsController.update(newsCenterId, news)
      } else {
        response = await NewsController.create(newsCenterId, news)
      }
      const { errors } = response

      if (errors) {
        this.setState({ errors: errors });
        Notification.notifyError(t('NewsModal::Oops something went wrong'))
      }
      else {
        Notification.notifySuccess(t('NewsModal::News successfully saved.'))
        if (onSubmit) onSubmit(response)
        close()
      }
    } catch (ex) {
      console.error(ex)
    } finally {
    }
  }

  onCloseClick() {
    this.props.close()
  }

  onErrorsDismiss() {
    this.setState({
      errors: {}
    })
  }

  async onCoverImageChange(acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) {
    try {
      const { news } = this.state
      const coverImage = acceptedFiles[0]

      ActiveStorageController.uploadPublic(coverImage, async (error, blob) => {
        if (error) { console.error(error) }
        if (blob) {
          const { url } = await ActiveStorageController.getBlobUrl(blob)

          this.setState({
            news: {
              ...news,
              cover_image_url: url,
            }
          })
        }
      })

    } catch (ex) {
      console.error(ex)
    }
  }

  onCoverImageDeleteClick() {
    const { t } = this.props
    const { news } = this.state
    const confirmed = confirm(t('NewsModal::Are you sure you want to remove this cover image?'))

    if (confirmed) {
      this.setState({
        news: {
          ...news,
          cover_image_url: null,
        }
      })
    }
  }

  onTitleChange(e) {
    const { news, activeLocale } = this.state

    const updatedTranslations = MobilityHelper.updateTranslation<NewsTranslateableFields>(activeLocale, news.translations, 'title', e.currentTarget.value)

    this.setState({
      news: {
        ...news,
        translations: updatedTranslations
      }
    })
  }

  onSummaryChange(e) {
    const { news, activeLocale } = this.state

    const updatedTranslations = MobilityHelper.updateTranslation<NewsTranslateableFields>(activeLocale, news.translations, 'summary', e.currentTarget.value)

    this.setState({
      news: {
        ...news,
        translations: updatedTranslations
      }
    })
  }

  onContentChange(content: string) {
    const { news, activeLocale } = this.state

    const updatedTranslations = MobilityHelper.updateTranslation<NewsTranslateableFields>(activeLocale, news.translations, 'content', content)

    this.setState({
      news: {
        ...news,
        translations: updatedTranslations
      }
    })
  }

  onLocaleChange(option) {
    this.setState({ activeLocale: option.value, })
  }

  onAuthorChange(option: any, value: User) {
    const { news } = this.state

    this.setState({
      news: {
        ...news,
        author: value,
        author_id: value ? value.id : null,
      }
    })
  }

  onTagsChange(tagIds?: string[], tags?: NewsTag[]) {
    const { news } = this.state

    this.setState({
      news: {
        ...news,
        tag_ids: tagIds || [],
        tags: tags || [],
      }
    })
  }

  onDeleteClick() {
    const { t, newsCenterId, news, onDelete, close } = this.props

    requestAnimationFrame(() => {
      this.props.showConfirmModal({
        title: t('NewsModal::Delete news'),
        description: t('NewsModal::You are about to delete this news. This news will be permanently deleted along with all the associated data. Are you sure?'),
        action: { label: t('NewsModal::Delete'), isDestructive: true },
        onConfirm: async () => {
          try {
            await NewsController.delete(newsCenterId, news.id)

            if (onDelete) onDelete(news)
            close()

            Notification.notifySuccess(t('NewsModal::News successfully deleted.'))
          } catch (ex) {
            console.error(ex)
          }
        }
      })
    })
  }

  onToggleSidepanel() {
    this.setState({ sidepanelOpen: !this.state.sidepanelOpen })
    this.setState({ mobileSidepanelOpen: !this.state.mobileSidepanelOpen })
  }

  render() {
    const { t, newsCenterId } = this.props
    const { didInitialLoad, errors, news, helpCenter, activeLocale, sidepanelOpen, mobileSidepanelOpen } = this.state

    const localeOptions = HelpCenterHelper.getSupportedLocaleOptions(helpCenter)
    const selectedLocaleOption = localeOptions.find(option => option.value === activeLocale)

    return (
      <>
        <Helmet>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin='anonymous' />
          <link href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap" rel="stylesheet"></link>
        </Helmet>

        <NewsModalWindow>
          {!didInitialLoad && <ModalLoaderContainer><ModalLoader /></ModalLoaderContainer>}
          {didInitialLoad && <>
            <NewsModalWindowContent>
              <div className='modal-head'>
                <div className='modal-header'>
                  <h3 className='modal-header-title'>
                    <span className='modal-header-title-text'>
                      {t('NewsModal::News')}
                    </span>
                  </h3>

                  <Actions>
                    {news?.id && <>
                      <Actions>
                        <SimpleTooltip content={t('NewsModal::Delete news')}>
                          <Action onClick={this.onDeleteClick}>
                            <Icon icon='trash' />
                          </Action>
                        </SimpleTooltip>
                      </Actions>

                      <ActionDivider />
                    </>}

                    <SimpleTooltip content={t('NewsModal::Close')}>
                      <Action onClick={this.onCloseClick}>
                        <Icon icon='close' />
                      </Action>
                    </SimpleTooltip>
                    {!sidepanelOpen && <SimpleTooltip content={t('NewsModal::Show details')}>
                      <Action type='secondary' onClick={this.onToggleSidepanel}>
                        <Icon icon='sidebar' />
                      </Action>
                    </SimpleTooltip>}
                    {!mobileSidepanelOpen && <SimpleTooltip content={t('NewsModal::Show details')}>
                      <Action type='secondary' onClick={this.onToggleSidepanel} mobile>
                        <Icon icon='sidebar' />
                      </Action>
                    </SimpleTooltip>}
                  </Actions>
                </div>
              </div>


              <ModalMiddle>
                <ModalContent>
                  <form onSubmit={this.onFormSubmit}>
                    {!news?.cover_image_url && <DropzoneContainer>
                      <Dropzone
                        dropzoneProps={{
                          onDrop: this.onCoverImageChange,
                          multiple: false,
                          maxSize: NEWS_COVER_IMAGE_FILE_SIZE,
                          accept: NEWS_COVER_IMAGE_CONTENT_TYPES
                        }}
                      />
                    </DropzoneContainer>}
                    {news.cover_image_url && <CoverImageContainer modalMode={true} onClick={this.onCoverImageDeleteClick}>
                      <CoverImage
                        src={news.cover_image_url}
                      />
                    </CoverImageContainer>}

                    {news.tags?.length > 0 && <NewsTags>
                      {news.tags.map(tag => {
                        return (
                          <NewsTagItem
                            key={tag.id}
                          >
                            {tag.name}
                          </NewsTagItem>
                        )
                      })}
                    </NewsTags>}

                    <Title>
                      <AutogrowTextarea
                        name='title'
                        minRows={1}
                        maxRows={100}
                        value={MobilityHelper.getTranslation(activeLocale, news?.translations, 'title')}
                        onChange={this.onTitleChange}
                        placeholder={t('NewsModal::Untitled news')}
                        lineHeight={38}
                      />
                    </Title>

                    <Summary>
                      <AutogrowTextarea
                        name='summary'
                        minRows={1}
                        maxRows={100}
                        value={MobilityHelper.getTranslation(activeLocale, news?.translations, 'summary')}
                        onChange={this.onSummaryChange}
                        lineHeight={24}
                        placeholder={t('NewsModal::Describe your news to help it get found')}
                      />
                    </Summary>

                    <ArticleContent>
                      <Editor
                        model={MobilityHelper.getTranslation(activeLocale, news?.translations, 'content')}
                        onModelChange={this.onContentChange}
                        config={{
                          ...ARTICLE_EDITOR_CONFIG,
                          placeholderText: t('NewsModal::Start writing...'),
                        }}
                      />
                    </ArticleContent>
                  </form>
                </ModalContent>
              </ModalMiddle>
              <ModalFooter>
                <div className='modal-footer'>
                  <div className='modal-footer-actions'>
                    <div key='main-action' className='popover-wrapper'>
                      <TooltipError
                        errors={errors}
                        onDismiss={this.onErrorsDismiss}
                      />
                      <input type='submit' style={{ display: 'none' }} />
                      <Button
                        type='success'
                        text={t('NewsModal::Save')}
                        onClick={this.onFormSubmit}
                      />
                    </div>
                  </div>
                </div>
              </ModalFooter>
            </NewsModalWindowContent>

            <NewsModalWindowDetails active={sidepanelOpen} mobileActive={mobileSidepanelOpen}>
              <div className='modal-head'>
                <div className='modal-header'>
                  <h3 className='modal-header-title'>
                    <span className='modal-header-title-text'>
                      {t('NewsModal::Details')}
                    </span>
                  </h3>

                  <Actions>
                    <SimpleTooltip content={t('NewsModal::Close')}>
                      <Action onClick={this.onCloseClick} mobile>
                        <Icon icon='close' />
                      </Action>
                    </SimpleTooltip>

                    <SimpleTooltip content={t('NewsModal::Hide details')}>
                      <Action type='secondary' onClick={this.onToggleSidepanel}>
                        <Icon icon='sidebar' />
                      </Action>
                    </SimpleTooltip>
                  </Actions>
                </div>
              </div>

              <ModalContent>
                <Details>
                  <DetailsItem>
                    <div>{t('NewsModal::News ID')}</div>
                    <div>{news?.id || '-'}</div>
                  </DetailsItem>
                  <DetailsItem stacked>
                    <div>{t('NewsModal::Language')}</div>
                    <PowerSelect
                      options={localeOptions}
                      value={selectedLocaleOption}
                      onChange={this.onLocaleChange}
                    />
                  </DetailsItem>

                  <DetailsItem stacked>
                    <div>{t('NewsModal::Author')}</div>
                    <ResourceCreatablePowerSelect
                      type='user'
                      value={news?.author_id}
                      onChange={this.onAuthorChange}
                      placeholder={t('NewsModal::Select author')}
                      isClearable
                    />
                  </DetailsItem>
                  <DetailsItem stacked>
                    <div>{t('NewsModal::Tags')}</div>
                    <ResourceCreatablePowerSelect
                      type='news_tag'
                      params={{ news_center_id: newsCenterId }}
                      createParams={{ news_center_id: newsCenterId }}
                      isMulti
                      value={news?.tag_ids}
                      placeholder={t('NewsModal::Add tags')}
                      onChange={this.onTagsChange}
                    />
                  </DetailsItem>
                  <DetailsItem>
                    <div>{t('NewsModal::Last updated')}</div>
                    <div>{news?.id ? moment(news.updated_at).fromNow() : '-'}</div>
                  </DetailsItem>
                  <DetailsItem>
                    <div>{t('NewsModal::Created')}</div>
                    <div>{news?.id ? moment(news.created_at).fromNow() : '-'}</div>
                  </DetailsItem>
                  {false && <DetailsItem stacked>
                    <div>{t('NewsModal::Reacted')}</div>
                    <div className='emoji'>
                      <span>😃 0%</span>
                      <span>😐 0%</span>
                      <span>😞 0%</span>
                    </div>
                  </DetailsItem>}
                </Details>
              </ModalContent>
            </NewsModalWindowDetails>
          </>}
        </NewsModalWindow>
      </>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    },
    modals: {
      newsModal: {
        locale,
        news,
        newsCenterId,
        onSubmit,
        onDelete,
      }
    }
  } = state

  return {
    locale: locale,
    newsCenterId: newsCenterId,
    news: news,
    onSubmit: onSubmit,
    onDelete: onDelete,
    currentUser: currentUser,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeNewsModal()),
    showConfirmModal: (options) => dispatch(showConfirmModal(options)),
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(NewsModal)) as any) as any